import React from 'react';
import PropTypes from 'prop-types';
import { ChipField as RAChipField, useRecordContext } from 'react-admin';
import { alpha, lighten, darken } from '@mui/material/styles';


const NAMED_COLORS = {
	green: {
		lightBg: '#d4edda',
		lightColor: '#155724',
		darkBg: 'rgba(16, 185, 129, 0.2)',
		darkColor: '#34d399'
	},
	yellow: {
		lightBg: '#fff3cd',
		lightColor: '#856404',
		darkBg: 'rgba(251, 191, 36, 0.2)',
		darkColor: '#fbbf24'
	},
	orange: {
		lightBg: '#fff3e0',
		lightColor: '#ef6c00',
		darkBg: 'rgba(255, 152, 0, 0.2)',
		darkColor: '#ffa726'
	},
	red: {
		lightBg: '#f8d7da',
		lightColor: '#721c24',
		darkBg: 'rgba(232, 107, 107, 0.2)',
		darkColor: '#E86B6B'
	},
	blue: {
		lightBg: '#e3f2fd',
		lightColor: '#1565c0',
		darkBg: 'rgba(33, 150, 243, 0.2)',
		darkColor: '#42a5f5'
	},
	default: {
		lightBg: '#EBEBEB',
		lightColor: '#616161',
		darkBg: 'rgba(158, 158, 158, 0.2)',
		darkColor: '#bdbdbd'
	}
};

const MUI_COLORS = [
	'primary',
	'secondary',
	'success',
	'warning',
	'error',
	'info'
];

const chipSx = {
	borderRadius: '6px',
	textTransform: 'unset',
	backgroundColor: (theme) => theme.palette.mode === 'dark' ? NAMED_COLORS.default.darkBg : NAMED_COLORS.default.lightBg,
	color: (theme) => theme.palette.mode === 'dark' ? NAMED_COLORS.default.darkColor : NAMED_COLORS.default.lightColor
};

const smallSx = {
	height: '20px'
};

const getChipColors = (color) => {
	const named = NAMED_COLORS[color];

	if (named) {
		return {
			backgroundColor: (theme) => theme.palette.mode === 'dark' ? named.darkBg : named.lightBg,
			color: (theme) => theme.palette.mode === 'dark' ? named.darkColor : named.lightColor
		};
	}

	if (MUI_COLORS.includes(color)) {
		return {
			backgroundColor: (theme) =>
				theme.palette.mode === 'dark'
					? alpha(theme.palette[color].main, 0.2)
					: lighten(theme.palette[color].main, 0.78),

			color: (theme) =>
				theme.palette.mode === 'dark'
					? lighten(theme.palette[color].main, 0.4)
					: darken(theme.palette[color].main, 0.6)
		};
	}

	const hex = color || NAMED_COLORS.default.lightColor;
	return {
		backgroundColor: (theme) =>
			theme.palette.mode === 'dark'
				? alpha(hex, 0.2)
				: lighten(hex, 0.8),

		color: (theme) =>
			theme.palette.mode === 'dark'
				? lighten(hex, 0.4)
				: darken(hex, 0.6)
	};
};

const ChipField = ({ source, color = 'default', size = 'small', sx = {}, ...props }) => {
	const record = useRecordContext(props);

	return (
		record && (record[source] !== undefined && record[source] !== null) ?
			<RAChipField sx={{ ...chipSx, ...(size === 'small' ? smallSx : {}), ...sx }} {...{ record, source, size, color, ...props }} />
			: null
	);
};

ChipField.propTypes = {
	source: PropTypes.string.isRequired,
	size: PropTypes.oneOf(["small", "medium"]),
	color: PropTypes.string
};

export default ChipField;
export { getChipColors };